import ReactOnRails from 'react-on-rails'
import * as React from 'react'
import { I18n } from 'i18n-js'
import { LocaleProps } from 'types/localisation'
import { useTranslation } from 'hooks/useTranslation'

export const TranslationContext = React.createContext<LocaleProps>({
  t: (key) => {
    if (['test', 'development'].includes(process.env.NODE_ENV || '')) {
      console.warn(
        'Translation Context used outside of translations provider/ withTranslations'
      )
    }

    return key
  },
  locale: 'en',
  currency: 'GBP',
  location: {
    name: 'United Kingdom',
    code: 'GB'
  },
  availableLocales: [],
  availableCurrencies: [],
  translations: {}
})

export function withTranslations<P>(
  WrappedComponent: React.ComponentType<P & Partial<LocaleProps>>
): React.FC<P> {
  return function WithTranslationsHoc(props: P): JSX.Element {
    const store = ReactOnRails.getStore('translationStore') as {
      getState: () => LocaleProps
    }
    const {
      locale,
      currency,
      location,
      translations,
      availableLocales,
      availableCurrencies
    } = store.getState()

    const { locale: contextLocale, translations: contextTranslations } =
      useTranslation()

    const i18n = React.useMemo(() => {
      return new I18n({
        [locale]: translations || contextTranslations
      })
    }, [locale, translations, contextTranslations])

    const value: LocaleProps = React.useMemo(
      () => ({
        t: (key, opt = {}) => i18n.t(key, opt),
        translations,
        currency,
        locale,
        location,
        availableLocales,
        availableCurrencies
      }),
      [
        translations,
        currency,
        locale,
        location,
        availableLocales,
        availableCurrencies,
        i18n
      ]
    )

    i18n.defaultLocale = 'en'
    i18n.locale = locale || contextLocale

    if (!locale || !translations) {
      return (
        <WrappedComponent
          {...props}
          translations={contextTranslations}
          locale={contextLocale}
          availableLocales={availableLocales}
          availableCurrencies={availableCurrencies}
        />
      )
    }

    return (
      <TranslationContext.Provider value={value}>
        <WrappedComponent
          {...props}
          availableLocales={availableLocales}
          availableCurrencies={availableCurrencies}
        />
      </TranslationContext.Provider>
    )
  }
}
